import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  TextField,
  Tooltip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import firebase from '../../utilities/firebase';
import store from '../../utilities/store';
import constants from '../../utilities/constants';
import { StateManager } from '../../utilities/stateManager';
import { useSelectMenuProps } from '../../utilities/ModalStackContext';
import ClockOutNotesDialog from '../ClockOutNotesDialog';
import moment from 'moment';

const formatHours = (seconds) => {
  if (!seconds || seconds < 0) return '0.00';
  return (seconds / 3600).toFixed(2);
};

const toInputValue = (ms) => {
  if (!ms) return '';
  return moment(ms).format('YYYY-MM-DDTHH:mm');
};

const fromInputValue = (v) => {
  if (!v) return null;
  const m = moment(v, 'YYYY-MM-DDTHH:mm', true);
  return m.isValid() ? m.valueOf() : null;
};

// Mocked Firestore-like timestamp so the existing toMillis() lookups in render don't change.
const mockTs = (ms) => ({ toMillis: () => ms });

const buildDemoLogs = (stockNumber) => {
  const now = Date.now();
  const MIN = 60 * 1000;
  const HOUR = 60 * MIN;
  return [
    {
      id: 'demo-1', mechanicId: 'u_jane', mechanicName: 'Jane Doe', rate: 45,
      startTime: mockTs(now - 23 * MIN), endTime: null,
      status: 'active', source: 'checkin', closedBy: null,
      car: stockNumber || 'STK-DEMO', serviceOrderId: stockNumber, serviceId: 'svc_jane',
    },
    {
      id: 'demo-2', mechanicId: 'u_jane', mechanicName: 'Jane Doe', rate: 45,
      startTime: mockTs(now - 5 * HOUR), endTime: mockTs(now - 3 * HOUR - 13 * MIN),
      status: 'closed', source: 'checkin', closedBy: 'mechanic',
      car: stockNumber, serviceOrderId: stockNumber, serviceId: 'svc_jane',
    },
    {
      id: 'demo-3', mechanicId: 'u_mike', mechanicName: 'Mike Smith', rate: 50,
      startTime: mockTs(now - 27 * HOUR), endTime: mockTs(now - 26 * HOUR - 15 * MIN),
      status: 'closed', source: 'checkin', closedBy: 'system-sweep',
      car: stockNumber, serviceOrderId: stockNumber, serviceId: 'svc_mike',
    },
    {
      id: 'demo-4', mechanicId: 'u_mike', mechanicName: 'Mike Smith', rate: 50,
      startTime: mockTs(now - 31 * HOUR), endTime: mockTs(now - 28 * HOUR - 45 * MIN),
      status: 'closed', source: 'manual-edit', closedBy: 'manual-edit',
      car: stockNumber, serviceOrderId: stockNumber, serviceId: 'svc_mike',
    },
    {
      id: 'demo-5', mechanicId: 'u_jane', mechanicName: 'Jane Doe', rate: 45,
      startTime: mockTs(now - 3 * 24 * HOUR - 5 * HOUR),
      endTime: mockTs(now - 3 * 24 * HOUR - 1 * HOUR - 38 * MIN),
      status: 'closed', source: 'checkin', closedBy: 'mechanic',
      car: stockNumber, serviceOrderId: stockNumber, serviceId: 'svc_jane',
    },
  ];
};

export default function TimeTab({ order, stockNumber }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [selectedMechanicId, setSelectedMechanicId] = useState('');
  const [clockOutTarget, setClockOutTarget] = useState(null);
  const [addMechanicId, setAddMechanicId] = useState('');
  const [addStart, setAddStart] = useState('');
  const [addEnd, setAddEnd] = useState('');
  const selectMenuProps = useSelectMenuProps();
  const isDemo = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('demo') === '1';

  // All active mechanics — admins/managers can clock anyone in, so this is not
  // gated by the per-mechanic clock_in_enabled opt-in (that only governs the
  // self-serve mechanic flows).
  const mechanicOptions = constants.makeSelects(
    'mechanics',
    { valueKey: 'id', labelKey: 'name' },
    (m) => m.is_active,
  );

  // The car stock number to clock into. ServiceOrderModal swaps order.car from
  // the stock string to the fully loaded car object once the car fetch lands, so
  // this has to handle both shapes. Sending the object straight through posted
  // `[object Object]` as stockNumber and the backend died on cars/.doc(<object>).
  // Never fall back to `stockNumber` — that prop is the orders doc id (SO1234),
  // which is not a car id.
  const clockStock = typeof order?.car === 'string'
    ? order.car
    : (order?.car?.stock || null);

  const handleClockInMechanic = async () => {
    if (!selectedMechanicId) {
      StateManager.setAlertAndOpen('Pick a mechanic to clock in', 'warning');
      return;
    }
    if (!clockStock) {
      StateManager.setAlertAndOpen('This service order has no car to clock into', 'error');
      return;
    }
    const mechanicName = mechanicOptions.find(m => m.value === selectedMechanicId)?.label || 'mechanic';
    setSaving('clockin');
    try {
      const res = await store.clockIn(selectedMechanicId, clockStock);
      if (!res || !res.success) throw new Error(res?.error || 'Clock-in failed');
      setSelectedMechanicId('');
      StateManager.setAlertAndOpen(`Clocked in ${mechanicName}`, 'success');
    } catch (e) {
      StateManager.setAlertAndOpen(e?.message || 'Clock-in failed', 'error');
    } finally {
      setSaving(null);
    }
  };

  // Back-date a completed shift. Clock-in always starts at "now", so the only way
  // to record hours somebody already worked used to be clock-in-then-edit-both-ends.
  // This writes the closed timeLog in one shot, against THIS service order.
  const handleAddEntry = async () => {
    if (!addMechanicId) {
      StateManager.setAlertAndOpen('Pick a mechanic for the time entry', 'warning');
      return;
    }
    const startMs = fromInputValue(addStart);
    const endMs = fromInputValue(addEnd);
    if (startMs == null || endMs == null) {
      StateManager.setAlertAndOpen('Enter both a start and an end time', 'warning');
      return;
    }
    if (endMs <= startMs) {
      StateManager.setAlertAndOpen('End time must be after the start time', 'warning');
      return;
    }
    const mechanic = constants.mechanics.find(m => m.id === addMechanicId);
    if (!mechanic) {
      StateManager.setAlertAndOpen('That mechanic is no longer on the roster', 'error');
      return;
    }
    if (!clockStock) {
      StateManager.setAlertAndOpen('This service order has no car to log time against', 'error');
      return;
    }
    if (isDemo) {
      StateManager.setAlertAndOpen('[Demo] Entry not saved', 'info');
      return;
    }
    setSaving('add');
    try {
      const ref = firebase.firestore().collection('timeLogs').doc();
      await ref.set({
        id: ref.id,
        serviceOrderId: stockNumber,
        serviceId: null,
        mechanicId: mechanic.id,
        mechanicName: mechanic.name,
        // Rate in effect on the clock-in day.
        rate: constants.rateOn(mechanic.id, moment(startMs).format('YYYY/MM/DD')),
        startTime: firebase.firestore.Timestamp.fromMillis(startMs),
        endTime: firebase.firestore.Timestamp.fromMillis(endMs),
        status: 'closed',
        source: 'manual-add',
        closedBy: 'manual-add',
        car: clockStock,
        createdBy: StateManager.userID || null,
        createdByName: StateManager.userName || null,
        createdAt: firebase.firestore.Timestamp.now(),
      });
      if (stockNumber) await store.syncSOAllocation(stockNumber);
      setAddMechanicId('');
      setAddStart('');
      setAddEnd('');
      StateManager.setAlertAndOpen(
        `Added ${formatHours((endMs - startMs) / 1000)} hrs for ${mechanic.name}`, 'success',
      );
    } catch (e) {
      console.error('Error adding time entry:', e);
      StateManager.setAlertAndOpen('Could not add the entry: ' + (e?.message || e), 'error');
    } finally {
      setSaving(null);
    }
  };

  const clockOutMechanic = async (mechanicId, note) => {
    const sessionId = clockOutTarget?.id;
    setClockOutTarget(null);
    if (note && sessionId) {
      // Fire-and-forget; cleanup completes in background and writes back to the timeLog.
      store.cleanServiceNote(sessionId, note).catch(err => console.error('cleanServiceNote failed:', err));
    }
    setSaving(mechanicId);
    try {
      const res = await store.clockOut(mechanicId);
      if (!res || !res.success) throw new Error(res?.error || 'Clock-out failed');
      StateManager.setAlertAndOpen('Clocked out', 'success');
    } catch (e) {
      StateManager.setAlertAndOpen(e?.message || 'Clock-out failed', 'error');
    } finally {
      setSaving(null);
    }
  };

  useEffect(() => {
    if (!stockNumber) return;
    setLoading(true);

    // Demo: skip Firestore and use a fixed-but-relative-to-now set of timeLogs.
    if (isDemo) {
      const rows = buildDemoLogs(stockNumber);
      rows.sort((a, b) => {
        const aMs = a.startTime && a.startTime.toMillis ? a.startTime.toMillis() : 0;
        const bMs = b.startTime && b.startTime.toMillis ? b.startTime.toMillis() : 0;
        return bMs - aMs;
      });
      setLogs(rows);
      setLoading(false);
      return;
    }

    const unsub = firebase.firestore()
      .collection('timeLogs')
      .where('serviceOrderId', '==', stockNumber)
      .onSnapshot(
        (snap) => {
          const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          rows.sort((a, b) => {
            const aMs = a.startTime && a.startTime.toMillis ? a.startTime.toMillis() : 0;
            const bMs = b.startTime && b.startTime.toMillis ? b.startTime.toMillis() : 0;
            return bMs - aMs;
          });
          setLogs(rows);
          setLoading(false);
        },
        (err) => {
          console.error('Error listening to timeLogs:', err);
          setLoading(false);
          StateManager.setAlertAndOpen('Error loading time entries: ' + (err?.message || err), 'error');
        }
      );
    return () => unsub();
  }, [stockNumber, isDemo]);

  const handleEdit = async (log, field, value) => {
    const ms = fromInputValue(value);
    if (ms == null) {
      StateManager.setAlertAndOpen('Invalid date/time', 'warning');
      return;
    }
    // An inverted range is silently worth 0 hours everywhere downstream, so reject
    // it here instead of writing a row that quietly stops paying.
    const otherMs = field === 'startTime'
      ? (log.endTime && log.endTime.toMillis ? log.endTime.toMillis() : null)
      : (log.startTime && log.startTime.toMillis ? log.startTime.toMillis() : null);
    if (otherMs != null) {
      const startMs = field === 'startTime' ? ms : otherMs;
      const endMs = field === 'startTime' ? otherMs : ms;
      if (endMs <= startMs) {
        StateManager.setAlertAndOpen('End time must be after the start time', 'warning');
        return;
      }
    }
    if (isDemo) {
      setLogs(prev => prev.map(l => {
        if (l.id !== log.id) return l;
        const next = { ...l, [field]: mockTs(ms), source: 'manual-edit' };
        if (field === 'endTime' && l.status === 'active') {
          next.status = 'closed';
          next.closedBy = 'manual-edit';
        }
        return next;
      }));
      StateManager.setAlertAndOpen('[Demo] Edit applied locally — not saved', 'info');
      return;
    }
    setSaving(log.id);
    try {
      const update = {
        [field]: firebase.firestore.Timestamp.fromMillis(ms),
        source: 'manual-edit',
      };
      // Closing an active row via end time edit also marks it closed.
      if (field === 'endTime' && log.status === 'active') {
        update.status = 'closed';
        update.closedBy = 'manual-edit';
      }
      // A log is priced at the rate in effect on its clock-in day.
      if (field === 'startTime' && constants.mechanics.some(m => m.id === log.mechanicId)) {
        update.rate = constants.rateOn(log.mechanicId, moment(ms).format('YYYY/MM/DD'));
      }
      await firebase.firestore().collection('timeLogs').doc(log.id).update(update);
      if (stockNumber) await store.syncSOAllocation(stockNumber);
    } catch (e) {
      console.error('Error saving time log:', e);
      StateManager.setAlertAndOpen('Save failed: ' + (e?.message || e), 'error');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (log) => {
    if (!window.confirm(`Delete this time entry (${log.mechanicName})?`)) return;
    if (isDemo) {
      setLogs(prev => prev.filter(l => l.id !== log.id));
      StateManager.setAlertAndOpen('[Demo] Deleted locally — not saved', 'info');
      return;
    }
    setSaving(log.id);
    try {
      await firebase.firestore().collection('timeLogs').doc(log.id).delete();
      if (stockNumber) await store.syncSOAllocation(stockNumber);
    } catch (e) {
      console.error('Error deleting time log:', e);
      StateManager.setAlertAndOpen('Delete failed: ' + (e?.message || e), 'error');
    } finally {
      setSaving(null);
    }
  };

  // Admin/manager control to clock a mechanic in to this service order. Hidden
  // in demo mode (no backend). Rendered in both the empty and populated states.
  const clockInControl = isDemo ? null : (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 1 }}>
          Clock in a mechanic
        </Typography>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="clock-in-mechanic-label">Mechanic</InputLabel>
          <Select
            labelId="clock-in-mechanic-label"
            label="Mechanic"
            value={selectedMechanicId}
            onChange={(e) => setSelectedMechanicId(e.target.value)}
            MenuProps={selectMenuProps}
            disabled={saving === 'clockin'}
          >
            {mechanicOptions.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving === 'clockin' ? <CircularProgress size={18} color="inherit" /> : <LoginIcon />}
          onClick={handleClockInMechanic}
          disabled={saving === 'clockin' || !selectedMechanicId || !clockStock}
        >
          Clock In
        </Button>
        {!clockStock && (
          <Typography variant="caption" color="warning.dark">
            No car on this service order yet — assign one to clock in.
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 1 }}>
          Add a past entry
        </Typography>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="add-entry-mechanic-label">Mechanic</InputLabel>
          <Select
            labelId="add-entry-mechanic-label"
            label="Mechanic"
            value={addMechanicId}
            onChange={(e) => setAddMechanicId(e.target.value)}
            MenuProps={selectMenuProps}
            disabled={saving === 'add'}
          >
            {mechanicOptions.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Started"
          type="datetime-local"
          size="small"
          value={addStart}
          onChange={(e) => setAddStart(e.target.value)}
          disabled={saving === 'add'}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
        />
        <TextField
          label="Ended"
          type="datetime-local"
          size="small"
          value={addEnd}
          onChange={(e) => setAddEnd(e.target.value)}
          disabled={saving === 'add'}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
        />
        <Button
          variant="outlined"
          color="primary"
          startIcon={saving === 'add' ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
          onClick={handleAddEntry}
          disabled={saving === 'add' || !addMechanicId || !addStart || !addEnd}
        >
          Add Entry
        </Button>
      </Paper>
    </>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (logs.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        {clockInControl}
        <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">No time entries on this service order yet.</Typography>
          <Typography variant="caption">
            Time entries are created when a mechanic clocks in — here, at /mechanic-clock, or from their
            orders page. Use "Add a past entry" above to record hours already worked.
          </Typography>
        </Box>
      </Box>
    );
  }

  const { totalSeconds, totalCost } = logs.reduce((acc, log) => {
    const start = log.startTime && log.startTime.toMillis ? log.startTime.toMillis() : null;
    const end = log.endTime && log.endTime.toMillis
      ? log.endTime.toMillis()
      : (log.status === 'active' ? Date.now() : null);
    if (!start || !end || end <= start) return acc;
    const seconds = (end - start) / 1000;
    return {
      totalSeconds: acc.totalSeconds + seconds,
      totalCost: acc.totalCost + (seconds / 3600) * (Number(log.rate) || 0),
    };
  }, { totalSeconds: 0, totalCost: 0 });
  const formatMoney = (n) => `$${(Math.round((n || 0) * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Box sx={{ py: 2 }}>
      {clockInControl}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Time Entries
          </Typography>
          {isDemo && (
            <Chip label="Demo data" size="small" color="info" variant="outlined" />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          <strong>{formatHours(totalSeconds)}</strong> hrs · <strong>{formatMoney(totalCost)}</strong> across {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Mechanic</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Ended</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => {
              const startMs = log.startTime && log.startTime.toMillis ? log.startTime.toMillis() : null;
              const endMs = log.endTime && log.endTime.toMillis
                ? log.endTime.toMillis()
                : (log.status === 'active' ? Date.now() : null);
              const seconds = startMs && endMs && endMs > startMs ? (endMs - startMs) / 1000 : 0;
              const isActive = log.status === 'active';
              const isSaving = saving === log.id;

              return (
                <TableRow key={log.id}>
                  <TableCell>
                    <Typography variant="body2">{log.mechanicName || log.mechanicId}</Typography>
                    <Typography variant="caption" color="text.secondary">{log.car}</Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="datetime-local"
                      size="small"
                      defaultValue={toInputValue(startMs)}
                      onBlur={(e) => {
                        const newVal = e.target.value;
                        if (newVal !== toInputValue(startMs)) handleEdit(log, 'startTime', newVal);
                      }}
                      disabled={isSaving}
                      inputProps={{ step: 60 }}
                    />
                  </TableCell>
                  <TableCell>
                    {isActive ? (
                      <Tooltip title="Set an end time to close this active session">
                        <TextField
                          type="datetime-local"
                          size="small"
                          defaultValue=""
                          onBlur={(e) => {
                            if (e.target.value) handleEdit(log, 'endTime', e.target.value);
                          }}
                          disabled={isSaving}
                          inputProps={{ step: 60 }}
                          placeholder="—"
                        />
                      </Tooltip>
                    ) : (
                      <TextField
                        type="datetime-local"
                        size="small"
                        defaultValue={toInputValue(endMs)}
                        onBlur={(e) => {
                          const newVal = e.target.value;
                          if (newVal !== toInputValue(endMs)) handleEdit(log, 'endTime', newVal);
                        }}
                        disabled={isSaving}
                        inputProps={{ step: 60 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">{formatHours(seconds)}</TableCell>
                  <TableCell align="right">${Number(log.rate || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    {isActive ? (
                      <Chip label="Active" size="small" color="success" />
                    ) : (
                      <Box>
                        <Chip
                          label={log.closedBy || 'closed'}
                          size="small"
                          color={log.closedBy === 'system-sweep' || log.closedBy === 'lunch-auto' ? 'warning' : 'default'}
                        />
                        {(log.source === 'manual-edit' || log.source === 'manual-add') && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {log.source === 'manual-add' ? 'added manually' : 'edited'}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                      {isActive && !isDemo && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<LogoutIcon fontSize="small" />}
                          onClick={() => setClockOutTarget(log)}
                          disabled={isSaving}
                        >
                          Clock Out
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(log)}
                        disabled={isSaving}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ClockOutNotesDialog
        open={!!clockOutTarget}
        allowSkip
        loading={saving === clockOutTarget?.mechanicId}
        onClose={() => setClockOutTarget(null)}
        onConfirm={(text) => clockOutMechanic(clockOutTarget.mechanicId, text)}
        onSkip={() => clockOutMechanic(clockOutTarget.mechanicId, null)}
      />
    </Box>
  );
}
